import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { noop } from 'lodash';
import { useUnmount } from '@cutwise/react-utils';
import { isEmpty } from '@cutwise/utils';

import Modal from '@/common/components/Modal/Modal';
import { Rollout } from '@/common/constants/rollouts';
import { withStores } from '@/common/hoc/withStores';
import { CollectionTreeItem } from '@/drive/entities/DriveTreeItem/CollectionTreeItem';
import { useForwardModalForm } from '@/drive/hooks/useForwardModalForm';
import ForwardDriveStore, { useForwardDriveStore } from '@/forwarding/components/ForwardModal/ForwardDriveStore';
import DriveModalTreeStore, {
  useDriveModalTreeStore,
} from '@/forwarding/components/ForwardModal/ForwardModalBody/DriveModalTree/DriveModalTreeStore';
import RecentDestinationsStore, {
  useRecentDestinationsStore,
} from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationsStore';
import ForwardModalFooter from '@/forwarding/components/ForwardModal/ForwardModalFooter/ForwardModalFooter';
import ForwardModalHeader from '@/forwarding/components/ForwardModal/ForwardModalHeader/ForwardModalHeader';
import { ForwardModalTab } from '@/forwarding/components/ForwardModal/ForwardModalTab';
import ForwardShareStore, { useForwardShareStore } from '@/forwarding/components/ForwardModal/ForwardShareStore';
import { ShareActionType } from '@/forwarding/constants/ShareActionType';
import { ForwardConfig } from '@/forwarding/entities/ForwardConfig/ForwardConfig';
import { useUserStore } from '@/user/stores/UserStore';

import styles from './ForwardModal.module.css';
import ForwardModalBody from './ForwardModalBody/ForwardModalBody';

export interface ForwardModalProps {
  config: Nullable<ForwardConfig>;
  usePortal?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function ForwardModal(props: ForwardModalProps) {
  const { config, usePortal, onClose, onSuccess } = props;
  const {
    isSubmitting: isDriveSubmitting,
    refresh,
    reset,
    addToRecentDestination,
    addToDriveTreeDestination,
  } = useForwardDriveStore();
  const {
    isSubmitting: isSpectrumSubmitting,
    refresh: refreshShare,
    reset: resetShare,
    execute: executeShareAction,
  } = useForwardShareStore();
  const {
    selected: recentDestination,
    setSelectedDestination,
    add: addRecentDestination,
  } = useRecentDestinationsStore();
  const { selected: driveTreeDestination } = useDriveModalTreeStore();
  const { isAuthorized, user } = useUserStore();
  const [activeTab, setActiveTab] = useState<ForwardModalTab>(ForwardModalTab.Recent);

  const hasDriveAccess = isAuthorized && !!user?.hasRollout(Rollout.Drive);
  const driveCommand = hasDriveAccess ? (config?.driveCommand ?? null) : null;
  const share = config?.share ?? null;
  const forwardInfo = config?.info ?? null;
  const destination = activeTab === ForwardModalTab.DriveTree ? driveTreeDestination : recentDestination;
  const hasAnnotation = !!forwardInfo && forwardInfo.preview.type !== 'pinnedItems';
  const isAnnotationShown = !!destination && hasAnnotation;
  const isFormReadOnly = (config?.isFormReadOnly ?? true) && !isAnnotationShown;

  const title = (
    <span className={styles.title}>
      <span>{config?.title ?? 'Forward to…'}</span>

      {hasAnnotation && <span className={styles.titleAnnotation}>...and say why</span>}
    </span>
  );
  const isCollectionDestinationOnly = config?.isCollectionDestinationOnly ?? false;

  const isSubmitting = isDriveSubmitting || isSpectrumSubmitting;

  useEffect(() => {
    if (driveCommand) {
      refresh(driveCommand, isCollectionDestinationOnly);
    } else {
      reset();
    }
  }, [driveCommand, isCollectionDestinationOnly, refresh, reset]);

  useEffect(() => {
    if (share) {
      refreshShare(share);
    } else {
      resetShare();
    }
  }, [refreshShare, resetShare, share]);

  useUnmount(resetShare);

  const { values, errors, validateForm, setFieldValue } = useForwardModalForm(noop, forwardInfo, {
    readOnly: isFormReadOnly,
  });

  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    setActiveTab(ForwardModalTab.Recent);
    onClose();
  }, [isSubmitting, onClose]);

  const handleSave = useCallback(async () => {
    if (!driveCommand || isSubmitting) {
      return;
    }

    const validationErrors = await validateForm();

    if (!isEmpty(validationErrors)) {
      return;
    }

    const res =
      activeTab === ForwardModalTab.DriveTree
        ? await addToDriveTreeDestination(driveCommand, values, isFormReadOnly)
        : await addToRecentDestination(driveCommand, values, isFormReadOnly);

    if (res && !res.hasErrors()) {
      onSuccess?.();
      handleClose();
    }
  }, [
    activeTab,
    addToDriveTreeDestination,
    addToRecentDestination,
    driveCommand,
    handleClose,
    isFormReadOnly,
    isSubmitting,
    onSuccess,
    validateForm,
    values,
  ]);

  const handleShareActionSelect = useCallback(
    async (action: ShareActionType) => {
      if (isSubmitting) {
        return;
      }

      if (action === ShareActionType.AddToSpectrum) {
        const validationErrors = await validateForm();

        if (!isEmpty(validationErrors)) {
          return;
        }
      }

      addRecentDestination(action);

      const res = await executeShareAction(action, values);

      if (action !== ShareActionType.QrCode && !res?.hasErrors()) {
        handleClose();
      }
    },
    [addRecentDestination, executeShareAction, handleClose, isSubmitting, validateForm, values],
  );

  return (
    <Modal
      isShowed={!!config}
      onClose={handleClose}
      usePortal={usePortal}
      disableClose={isSubmitting}
      fitWidthByContent
      title={title}
      contentClassName={styles.forwardModal}
      headerClassName={styles.forwardModalHeader}
      closeClassName={styles.forwardModalClose}
      bodyContentClassName={styles.forwardModalBody}
      childrenClassName={styles.forwardModalChildren}
      footerClassName={styles.footerBorderless}
      renderModalFooter={() => (
        <ForwardModalFooter
          onSave={handleSave}
          onClose={handleClose}
          activeTab={activeTab}
          isSaveShown={activeTab !== ForwardModalTab.Share}
          isSubmitting={isSubmitting}
        />
      )}
    >
      <div className={styles.body}>
        <div className={styles.header}>
          <ForwardModalHeader
            preview={forwardInfo?.preview ?? null}
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            readOnly={!isAnnotationShown}
            isTitleReadOnly={destination instanceof CollectionTreeItem}
          />
        </div>

        <div className={styles.content}>
          <ForwardModalBody
            hasDriveTree={!!driveCommand}
            activeTab={activeTab}
            shareConfig={share}
            onTabChange={setActiveTab}
            onRecentDestinationSelect={setSelectedDestination}
            onShareActionSelect={handleShareActionSelect}
          />
        </div>
      </div>
    </Modal>
  );
}

export default withStores(
  ({
    userStore,
    analyticsStore,
    driveModifierStore,
    driveTreeStore,
    collectionPreviewStore,
    entityAccessEventEmitter,
  }) => {
    const forwardShareStore = new ForwardShareStore(userStore, analyticsStore);
    const recentDestinationsStore = new RecentDestinationsStore(userStore, driveTreeStore, forwardShareStore);
    const driveModalTreeStore = new DriveModalTreeStore(driveModifierStore, driveTreeStore);

    return {
      forwardDriveStore: new ForwardDriveStore(
        driveModifierStore,
        recentDestinationsStore,
        driveModalTreeStore,
        collectionPreviewStore,
        entityAccessEventEmitter,
      ),
      recentDestinationsStore,
      driveModalTreeStore,
      forwardShareStore,
    };
  },
  observer(ForwardModal),
);
